import { Carousel, Drawer, Modal } from "antd";
import { DsaBoardDto, DsaPlaylistEntryDto } from "../../generated/dashboard-client";
import "./PreviewModal.scss";
import { CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { isProdEnv, isStageEnv } from "../../util/constants";
import { useRecoilValue } from "recoil";
import { mobileState } from "../../state/atoms";
import { getS3Url, isVideoPath } from "../../util/utils";
import { DisplayUploadDto } from "../../domain/DisplayUploadDto";

interface PreviewModalProps {
  isVisible: boolean;
  onClose: () => void;
  previewType: string;
  openMobileMenu?: () => void;
  previewBoard?: DsaBoardDto;
  previewEntries?: DsaPlaylistEntryDto[];
  previewUploadId?: string;
  previewUploadPath?: string;
  uploads?: DisplayUploadDto[];
}

export default function PreviewModal({ previewType, openMobileMenu = undefined, previewBoard = undefined, previewEntries = undefined, previewUploadId = undefined, previewUploadPath = undefined, uploads = [], isVisible, onClose }: PreviewModalProps) {
  const [slideNum, setSlideNum] = useState(1);
  const [iFrameUrls, setIFrameUrls] = useState([] as string[]);
  const mobile = useRecoilValue(mobileState);

  function getDesignUrl(designId: string, page: number) {
    return `${isProdEnv ? 'https://ohbz.com/dp/dsa/' : isStageEnv ? 'https://stage.mhme.nu/dp/dsa/' : 'https://test.mhmfun.com/lpv2/dp/dsa/'}${designId}?page=${page}&preview=true`;
  }

  function getUploadUrl(uploadId: string) {
    return `${isProdEnv ? 'https://ohbz.com/dp/dsa/upload/' : isStageEnv ? 'https://stage.mhme.nu/dp/dsa/upload/' : 'https://test.mhmfun.com/lpv2/dp/dsa/upload/'}${uploadId}?preview=true`;
  }

  useEffect(() => {
    if (isVisible && previewType === 'playlist' && previewEntries) {
      let toBeUrls = [];
      const firstEntry = previewEntries[0];
      const firstUrl = firstEntry.type === 'UPLOAD' ? getUploadUrl(firstEntry.uploadId!) : getDesignUrl(firstEntry.designId!, firstEntry.page!);
      toBeUrls.push(firstUrl);
      for (let i = 0; i < previewEntries.length - 1; i++) {
        toBeUrls.push('');
      }
      setIFrameUrls(toBeUrls);
    }
  }, [isVisible, previewType, previewEntries])

  function afterCarouselChange(num: number) {
    if (iFrameUrls[num] === '') {
      const nextEntry = previewEntries![num];
      iFrameUrls[num] = nextEntry.type === 'UPLOAD' ? getUploadUrl(nextEntry.uploadId!) : getDesignUrl(nextEntry.designId!, nextEntry.page!);
      setIFrameUrls([...iFrameUrls]);
    }
    setSlideNum(num + 1);
  }

  function onBackClick() {
    if (openMobileMenu) openMobileMenu();
    onClose();
  }

  // Upload entries can be videos, which the qr upload page can't render. Play those directly from S3.
  function getEntryVideoSrc(entry: DsaPlaylistEntryDto) {
    if (entry.type !== 'UPLOAD') return null;
    const upload = uploads.find(u => u.id === entry.uploadId);
    return upload && isVideoPath(upload.path) ? getS3Url(upload.path) : null;
  }

  const content = (<>
    {mobile && <div className="back-btn close-btn" onClick={() => onBackClick()}><LeftOutlined /></div>}
    <div className="close-btn" onClick={() => onClose()}><CloseOutlined /></div>
    <h2>Preview</h2>
    {previewType === 'playlist' && <div className="playlist-note">
      Note: Playlist transitions not shown in preview
    </div>}
    {previewType === 'playlist' && previewEntries &&
      <div className="playlist-frame">
        <Carousel fade autoplay autoplaySpeed={5000} pauseOnHover={false} speed={100} dots={false} lazyLoad="ondemand"
          arrows={true}
          prevArrow={<LeftOutlined />}
          nextArrow={<RightOutlined />}
          afterChange={(num) => afterCarouselChange(num)}
        >
          {previewEntries.map((_entry, idx) => {
            const videoSrc = getEntryVideoSrc(_entry);
            if (videoSrc) {
              return <video key={_entry.id}
                src={videoSrc}
                autoPlay
                muted
                loop
                playsInline
              />
            }
            return <iframe key={_entry.id}
              src={iFrameUrls[idx]}
              title="DSA Preview"
            />
          })}
        </Carousel>
        <div className="slide-num">{slideNum} / {previewEntries.length}</div>
      </div>}
    {previewType === 'board' && previewBoard &&
      <div className="board-frame">
        <iframe
          title="DSA Preview"
          src={getDesignUrl(previewBoard.designId!, previewBoard.page!)}
        />
      </div>}
    {previewType === 'upload' && previewUploadId &&
      <div className="board-frame">
        {isVideoPath(previewUploadPath)
          ? <video
              src={getS3Url(previewUploadPath!)}
              controls
              autoPlay
              muted
              loop
              playsInline
            />
          : <iframe
              title="DSA Preview"
              src={getUploadUrl(previewUploadId)}
            />}
      </div>}
  </>)

  return (
    !mobile ? 
    <Modal
      className="clean preview-modal"
      visible={isVisible}
      onCancel={() => onClose()}
      width={902}
      zIndex={1010}
    >
      {content}
    </Modal>
    :
    <Drawer
      placement={"bottom"}
      visible={isVisible}
      className={"mobile-bottom-menu preview-modal"}
      height={"80vh"}
      closeIcon={false}
      maskClosable={true}
      onClose={() => onClose()}
      zIndex={1001}
    >
        {content}
    </Drawer>
  )
}
